import { CAPTURE, DEATHBOMB, POLARITY, SUICIDE } from '../../../config/constants.js';

// Warm off-white used for "impact" sparks everywhere — hot enough to read
// as energy against any arena palette without introducing a new hue per hit.
const HIT_SPARK_COLOR = '#ffe9a8';
// Aria's card/sprite accent (data/characters.js `color`) — the cascade should
// read as hers at a glance, not as a generic explosion.
const REFRAIN_COLOR = '#c9a0ff';
const TIME_SCALE_EASE_RATE = 9; // per second, in REAL time — how fast slow-mo eases in/out

/**
 * Central home for everything "game feel": screen shake, hit-stop, slow-mo,
 * particle bursts, damage numbers, floating text, screen flashes, and the
 * ephemeral animation state the renderer reads (dash afterimages, boss
 * motion, pulses). Extracted from Game.js's old _wireJuice() so every
 * event -> feedback reaction lives in one place and is unit-testable
 * without a DOM.
 *
 * Owns state + event reactions only — rendering stays in Game.js's _draw*
 * methods, which read the public fields here. Nothing in this file affects
 * gameplay: every field is cosmetic. Two events with real gameplay halves
 * (minion:exploded's damage, minion:split's child spawns) keep those
 * halves in Game.js; only their visual halves live here.
 *
 * Accessibility. Two independent switches, because they cover two unrelated
 * disabilities and used to be one:
 *   - `save.reduceShake` (vestibular / motion sickness): trauma scaled to
 *     25%, hit-stop skipped outright, slow-mo damped to 25% of its depth.
 *   - `save.reduceFlash` (photosensitivity): screen flashes skipped entirely.
 */
export class JuiceDirector {
  constructor(eventBus, { camera, save, particles, getPlayer, getBoss, fxColor = null }) {
    this._camera = camera;
    this._save = save;
    this._particles = particles;
    this._getPlayer = getPlayer;
    this._getBoss = getBoss;
    // Equipped FX cosmetic color (data/cosmetics.js), or null for the default
    // per-event colors. Recolors the level-up and boss-kill bursts only.
    this._fxColor = fxColor;

    // --- time manipulation (consumed by Game._tick via tickTimeScale) ---
    this.hitStopRemaining = 0;
    this._timeScale = 1;
    this._timeScaleTarget = 1;
    this._slowMoRemaining = 0;

    // --- renderer-facing ephemeral state (all cosmetic) ---
    this.dashAfterimages = [];
    this.damageNumbers = [];
    this.floatingTexts = [];
    this.playerPulses = [];
    this.playerScalePop = 0;
    this.healGlowTimer = 0;
    this.phasePulse = 0;
    this.telegraphCallout = null;
    // { name, glimmer, grazes, desperation, remaining, maxLife } — the CAPTURED
    // title card (BULLET-HELL.md §D1), drawn by Game._drawCaptureBanner.
    this.captureBanner = null;
    this.bossMotion = { idleTime: 0, attackTime: 0 };
    this.screenFlash = null; // { color, strength, life, maxLife } — see Game._drawScreenEffects
    // { remaining, maxLife } while a hit is caught in the deathbomb window
    // (BULLET-HELL.md §D3) — Game._drawDeathbombPrompt draws the closing ring.
    this.deathbombPrompt = null;
    this.dashRefillFlash = 0; // blinks the just-recharged dash pip

    this._wire(eventBus);
  }

  // Scaled down (not skipped outright) under the "reduce shake/flash"
  // setting — keeps some hit feedback for photosensitivity-safe play
  // without the full-strength camera kick.
  addTrauma(amount) {
    this._camera.addTrauma(this._save.reduceShake ? amount * 0.25 : amount);
  }

  triggerHitStop(duration) {
    if (this._save.reduceShake) return;
    // Refresh to the new duration rather than stacking — rapid hits (e.g.
    // a pierced shot landing on two targets) should freeze once, not compound.
    this.hitStopRemaining = Math.max(this.hitStopRemaining, duration);
  }

  /** Eases the whole sim toward `scale` for `duration` real seconds, then back to 1. Deeper of two overlapping requests wins. */
  slowMo(scale, duration) {
    const target = this._save.reduceShake ? 1 - (1 - scale) * 0.25 : scale;
    this._timeScaleTarget = Math.min(this._timeScaleTarget, target);
    this._slowMoRemaining = Math.max(this._slowMoRemaining, duration);
  }

  /** Brief full-screen tint (see Game._drawScreenEffects). Skipped entirely under reduceFlash — a flash is exactly the photosensitivity concern that setting exists for. */
  flashScreen(color, strength, duration) {
    if (this._save.reduceFlash) return;
    this.screenFlash = { color, strength, life: duration, maxLife: duration };
  }

  addDamageNumber(x, y, text, color, life, rise) {
    this.damageNumbers.push({ x, y, text, color, life, maxLife: life, rise });
  }

  /** World-space (x, y) is the spawn point — see Game._drawFloatingTexts for how it drifts/fades from there. */
  addFloatingText(x, y, text, color) {
    const life = 1.1;
    this.floatingTexts.push({ x, y, text, color, life, maxLife: life });
  }

  /** A ring that expands outward from the player and fades as it grows, paired with a brief sprite scale-pop — revive/ultimate moments. */
  addPlayerPulse(color, maxRadius = 30, life = 0.5) {
    const player = this._getPlayer();
    this.playerPulses.push({ x: player.x, y: player.y, color, maxRadius, life, maxLife: life });
    this.playerScalePop = 1;
  }

  /** The same expanding ring anchored anywhere in the world — for bursts that happen away from the player (a dash landing, a slam). */
  addPulse(x, y, color, maxRadius = 30, life = 0.5) {
    this.playerPulses.push({ x, y, color, maxRadius, life, maxLife: life });
  }

  /**
   * Called once per rendered frame with the real elapsed frame time.
   * Returns the factor Game._tick multiplies frameTime by before feeding
   * the fixed-timestep accumulator: 0 during hit-stop (the old
   * freeze-the-accumulator behavior, unchanged), otherwise the eased
   * slow-mo scale (exactly 1 when nothing is active). Hit-stop always wins
   * over slow-mo — it snaps to a full freeze for its duration, then hands
   * back to whatever the slow-mo was doing. Both timers count down in REAL
   * time: a freeze or slow-mo beat should last the same wall-clock length
   * regardless of any other time manipulation.
   */
  tickTimeScale(frameTime) {
    if (this.hitStopRemaining > 0) {
      this.hitStopRemaining -= frameTime;
      return 0;
    }
    if (this._slowMoRemaining > 0) {
      this._slowMoRemaining -= frameTime;
      if (this._slowMoRemaining <= 0) this._timeScaleTarget = 1;
    }
    const diff = this._timeScaleTarget - this._timeScale;
    if (diff !== 0) {
      this._timeScale += diff * Math.min(1, TIME_SCALE_EASE_RATE * frameTime);
      if (Math.abs(this._timeScaleTarget - this._timeScale) < 0.01) {
        this._timeScale = this._timeScaleTarget;
      }
    }
    return this._timeScale;
  }

  /**
   * Ages all ephemeral state. Driven from Game._update at sim rate (not
   * from _tick) — deliberate, so cosmetics freeze during hit-stop and slow
   * down with slow-mo along with the rest of the world, exactly as the old
   * inline decay loops did.
   */
  update(dt) {
    this.bossMotion.idleTime += dt;
    this.bossMotion.attackTime = Math.max(0, this.bossMotion.attackTime - dt);
    this.phasePulse = Math.max(0, this.phasePulse - dt);
    // Quick decay — a snappy pop rather than a lingering wobble.
    this.playerScalePop = Math.max(0, this.playerScalePop - dt * 4);
    this.healGlowTimer = Math.max(0, this.healGlowTimer - dt);
    this.dashRefillFlash = Math.max(0, this.dashRefillFlash - dt);
    for (let i = this.dashAfterimages.length - 1; i >= 0; i--) {
      this.dashAfterimages[i].life -= dt;
      if (this.dashAfterimages[i].life <= 0) this.dashAfterimages.splice(i, 1);
    }
    for (let i = this.damageNumbers.length - 1; i >= 0; i--) {
      this.damageNumbers[i].life -= dt;
      if (this.damageNumbers[i].life <= 0) this.damageNumbers.splice(i, 1);
    }
    for (let i = this.playerPulses.length - 1; i >= 0; i--) {
      this.playerPulses[i].life -= dt;
      if (this.playerPulses[i].life <= 0) this.playerPulses.splice(i, 1);
    }
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      this.floatingTexts[i].life -= dt;
      if (this.floatingTexts[i].life <= 0) this.floatingTexts.splice(i, 1);
    }
    if (this.screenFlash) {
      this.screenFlash.life -= dt;
      if (this.screenFlash.life <= 0) this.screenFlash = null;
    }
    if (this.telegraphCallout) {
      this.telegraphCallout.remaining -= dt;
      if (this.telegraphCallout.remaining <= 0) this.telegraphCallout = null;
    }
    if (this.captureBanner) {
      this.captureBanner.remaining -= dt;
      if (this.captureBanner.remaining <= 0) this.captureBanner = null;
    }
    // Ticked on the same sim dt as Player's own pending-hit timer, so the
    // ring closing on screen is the actual window closing, not an
    // approximation of it — including while the slow-mo stretches both.
    if (this.deathbombPrompt) {
      this.deathbombPrompt.remaining -= dt;
      if (this.deathbombPrompt.remaining <= 0) this.deathbombPrompt = null;
    }
  }

  _wire(eventBus) {
    // --- landing hits ---
    eventBus.on('boss:damaged', ({ amount }) => {
      const boss = this._getBoss();
      if (!boss) return;
      this._particles.burst(boss.x, boss.y, { count: 8, color: '#ff5d6c', life: 0.3, speedMin: 40, speedMax: 130, size: 1.5 });
      this.addTrauma(0.15);
      this.triggerHitStop(0.03);
      this.addDamageNumber(boss.x, boss.y - boss.radius - 3, `-${Math.ceil(amount)}`, '#ffd166', 0.65, 12);
    });
    // Minions previously had zero hit/death feedback at all (only the
    // boss did) — landing a hit on one looked like nothing happened.
    eventBus.on('minion:damaged', ({ x, y, amount }) => {
      this._particles.burst(x, y, { count: 5, color: '#ff5d6c', life: 0.22, speedMin: 25, speedMax: 80, size: 1 });
      this.addDamageNumber(x, y - 6, `-${Math.ceil(amount)}`, '#f4e04d', 0.4, 8);
    });
    // Directional impact sparks at the projectile's actual contact point,
    // biased along its travel direction (see CombatSystem._checkCollisions)
    // — the radial bursts above come from the enemy's center, this is the
    // "metal on metal" glint at the point of contact.
    eventBus.on('combat:hit', ({ x, y, dirX, dirY }) => {
      this._particles.burst(x, y, {
        count: 3, color: HIT_SPARK_COLOR, life: 0.16, speedMin: 70, speedMax: 170, size: 1.1,
        shape: 'spark', additive: true, angle: Math.atan2(dirY, dirX), spread: 1.0,
      });
    });

    // --- deaths ---
    eventBus.on('minion:died', ({ x, y }) => {
      this._particles.burst(x, y, { count: 12, color: '#ff5d6c', life: 0.4, speedMin: 30, speedMax: 140, size: 1.5 });
      this._particles.burst(x, y, { count: 6, color: HIT_SPARK_COLOR, life: 0.25, speedMin: 80, speedMax: 190, size: 1.2, shape: 'spark', additive: true });
    });
    eventBus.on('boss:died', () => {
      const boss = this._getBoss();
      if (boss) {
        // Three layered bursts: the old red core, hot sparks, and glowing
        // debris that slows and settles (drag + gravity) — a boss death is
        // the biggest single moment in a run and should look like one.
        this._particles.burst(boss.x, boss.y, { count: 40, color: this._fxColor ?? '#ff5d6c', life: 0.8, speedMin: 30, speedMax: 200, size: 2.5 });
        this._particles.burst(boss.x, boss.y, { count: 30, color: HIT_SPARK_COLOR, life: 0.55, speedMin: 120, speedMax: 320, size: 1.6, shape: 'spark', additive: true });
        this._particles.burst(boss.x, boss.y, { count: 20, color: '#ffffff', life: 1.1, speedMin: 20, speedMax: 90, size: 1.8, shape: 'square', additive: true, drag: 1.6, gravity: 30 });
      }
      this.addTrauma(1);
      this.triggerHitStop(0.15);
      this.slowMo(0.3, 0.55);
      this.flashScreen('#ffffff', 0.28, 0.35);
    });
    // A herald also emits minion:damaged/minion:died alongside these (see
    // Boss.js's isMiniboss branch), so it already gets the baseline minion
    // treatment for free — this layers boss-shaped weight on top, between
    // a regular minion's feedback and a real boss:died's full spectacle.
    eventBus.on('miniboss:damaged', () => {
      this.addTrauma(0.08);
    });
    eventBus.on('miniboss:died', ({ boss }) => {
      this._particles.burst(boss.x, boss.y, { count: 30, color: '#ff5d6c', life: 0.5, speedMin: 30, speedMax: 160, size: 2 });
      this._particles.burst(boss.x, boss.y, { count: 14, color: HIT_SPARK_COLOR, life: 0.4, speedMin: 90, speedMax: 260, size: 1.4, shape: 'spark', additive: true });
      this.addTrauma(0.6);
      this.triggerHitStop(0.08);
      this.slowMo(0.55, 0.3);
      this.flashScreen('#ffffff', 0.12, 0.22);
    });

    // --- taking hits ---
    eventBus.on('player:damaged', () => {
      const player = this._getPlayer();
      this._particles.burst(player.x, player.y, { count: 8, color: '#ff8a4a', life: 0.35, speedMin: 40, speedMax: 120, size: 1.5 });
      this.addTrauma(0.35);
      this.triggerHitStop(0.06);
      this.addDamageNumber(player.x, player.y - 8, 'HIT', '#ff8a4a', 0.45, 5);
      this.flashScreen('#ff3b3b', 0.16, 0.18);
    });
    // Deathbomb (BULLET-HELL.md §D3). The single loudest non-death cue in
    // the game, on purpose: the whole mechanic is worthless if the player
    // can't tell the window opened, and it lasts a sixth of a second. The
    // slow-mo is not decoration — Player ticks the window on sim time, so
    // this is what turns 0.16s into something like 0.4s of wall clock to
    // react in. Gold throughout: the same hue as the full meter it spends.
    eventBus.on('player:deathbomb-window', ({ window: w, x, y }) => {
      this.deathbombPrompt = { remaining: w, maxLife: w };
      this.slowMo(DEATHBOMB.slowMoScale, DEATHBOMB.slowMoDuration);
      this.flashScreen(DEATHBOMB.color, 0.22, 0.16);
      this.addTrauma(0.2);
      this._particles.burst(x, y, { count: 10, color: DEATHBOMB.color, life: 0.3, speedMin: 30, speedMax: 110, size: 1.4, shape: 'spark', additive: true });
    });
    // The bomb itself landing. Sized above the window cue and above an
    // ordinary ultimate: this is the best thing that happens in a fight, and
    // the player needs to know beyond doubt that the hit is gone. (The
    // ultimate's own cues and D2's bullet cancel fire underneath it.)
    eventBus.on('player:deathbombed', ({ x, y }) => {
      this.deathbombPrompt = null;
      this.addDamageNumber(x, y - 12, 'DEATHBOMB', DEATHBOMB.color, 0.9, 14);
      this.addPlayerPulse(DEATHBOMB.color, 46, 0.45);
      this.flashScreen('#ffffff', 0.3, 0.22);
      this.addTrauma(0.45);
      this._particles.burst(x, y, { count: 22, color: DEATHBOMB.color, life: 0.5, speedMin: 60, speedMax: 200, size: 1.8, shape: 'spark', additive: true });
    });
    eventBus.on('player:dodged', () => {
      const player = this._getPlayer();
      this.addDamageNumber(player.x, player.y - 8, 'DODGE', '#4ade80', 0.55, 8);
      this._particles.burst(player.x, player.y, { count: 5, color: '#4ade80', life: 0.25, speedMin: 20, speedMax: 60, size: 1 });
    });
    // Graze near-miss: a couple of gold sparks at the bullet, matching the
    // ultimate meter it feeds. Deliberately tiny with no shake/flash/text —
    // dense patterns graze several bullets a second.
    eventBus.on('bullet:grazed', ({ x, y }) => {
      this._particles.burst(x, y, { count: 2, color: '#ffd166', life: 0.18, speedMin: 30, speedMax: 90, size: 0.8, shape: 'spark', additive: true });
    });

    // Absorbing a matched-tone bullet (BULLET-HELL.md §D7). Sized exactly like
    // the graze above and for the same reason — absorbs arrive in ring-fuls —
    // but in the resonance violet, so "that one paid me" is legible without
    // reading the number.
    eventBus.on('polarity:absorbed', ({ x, y }) => {
      this._particles.burst(x, y, { count: 3, color: POLARITY.color, life: 0.2, speedMin: 25, speedMax: 80, size: 0.9, shape: 'spark', additive: true });
    });
    // The release. This one DOES get the flourish: it is the payoff the whole
    // mechanic banks toward, it happens a handful of times a fight, and the
    // player needs to know their volley left without looking away from the
    // curtain. Pulse + light trauma, no hit-stop (the shots still have to steer).
    eventBus.on('polarity:released', ({ x, y }) => {
      this._particles.burst(x, y, { count: 14, color: POLARITY.color, life: 0.38, speedMin: 60, speedMax: 190, size: 1.6, additive: true });
      this.addDamageNumber(x, y - 10, 'RELEASE', POLARITY.color, 0.6, 10);
      this.addPlayerPulse(POLARITY.color, 26, 0.3);
      this.addTrauma(0.12);
    });

    // Harmonics fusion: two weapons becoming one is a build-defining
    // moment — give it an ultimate-grade flourish at the player.
    // Big centered AoE bursts (Nova Pulse) — opt-in via `weapon.lastBurstRadius`
    // (see CombatSystem's fireFlash block). A screen-filling burst used to get
    // the exact same tiny feedback as a single dart tick; this scales the
    // reaction to the burst's actual radius and reuses addPlayerPulse — an
    // expanding-ring-from-the-player hook that already existed but no weapon
    // used, and is literally the same shape as the burst itself.
    eventBus.on('weapon:aoe-burst', ({ x, y, radius, color }) => {
      this._particles.burst(x, y, { count: 16, color, life: 0.4, speedMin: 50, speedMax: Math.min(260, radius * 2), size: 2, additive: true });
      this._particles.burst(x, y, { count: 10, color: HIT_SPARK_COLOR, life: 0.22, speedMin: 90, speedMax: 220, size: 1.2, shape: 'spark', additive: true });
      this.addTrauma(0.25);
      this.addPlayerPulse(color, radius, 0.35);
    });

    // Aria's Refrain (data/characters.js) carrying out of a corpse. Deliberately
    // quieter than an AoE burst and with no trauma: a good cascade fires this
    // many times in a few frames, and screen shake per link would be a mess.
    // The ring is what sells it — it reads as the echo's reach, so the player
    // can see how far the next carry can travel.
    eventBus.on('refrain:carry', ({ x, y, radius }) => {
      this._particles.burst(x, y, { count: 8, color: REFRAIN_COLOR, life: 0.34, speedMin: 40, speedMax: Math.min(180, radius * 1.6), size: 1.6, additive: true });
      this.addPulse(x, y, REFRAIN_COLOR, radius, 0.28);
    });

    // A Harmonic just came into reach (BULLET-HEAVEN.md §H4). Deliberately
    // sized BELOW the fusion itself below — this is the announcement that the
    // chorus is coming, not the chorus. Its whole job is to make the fusion
    // card, which arrives a level or two later on its high pool weight, land
    // as something the player was already waiting for.
    eventBus.on('fusion:eligible', ({ name }) => {
      const player = this._getPlayer();
      this.addFloatingText(player.x, player.y - player.radius - 14, `${name.toUpperCase()} — HARMONIC READY`, '#c9a8ff');
      this._particles.burst(player.x, player.y, { count: 14, color: '#c9a8ff', life: 0.6, speedMin: 20, speedMax: 90, size: 1.4, additive: true });
      this.addPlayerPulse('#c9a8ff', 26, 0.55);
      this.flashScreen('#c9a8ff', 0.1, 0.25);
    });

    eventBus.on('weapon:fused', () => {
      const player = this._getPlayer();
      this._particles.burst(player.x, player.y, { count: 24, color: '#c9a8ff', life: 0.5, speedMin: 30, speedMax: 140, size: 1.8 });
      this._particles.burst(player.x, player.y, { count: 12, color: '#ffffff', life: 0.35, speedMin: 60, speedMax: 200, size: 1.2, shape: 'spark', additive: true });
      this.addTrauma(0.25);
    });

    // --- Echo Duel (data/echoBoss.js) ---
    eventBus.on('echo-rift:spawned', ({ x, y }) => {
      this._particles.burst(x, y, { count: 10, color: '#c084fc', life: 0.5, speedMin: 10, speedMax: 50, size: 1.2, additive: true });
    });
    eventBus.on('echo-duel:accepted', () => {
      const player = this._getPlayer();
      this._particles.burst(player.x, player.y, { count: 20, color: '#c084fc', life: 0.5, speedMin: 40, speedMax: 160, size: 1.5, shape: 'spark', additive: true });
      this.addTrauma(0.3);
    });
    eventBus.on('echo-duel:won', ({ reward, x, y }) => {
      this._particles.burst(x, y, { count: 24, color: '#ffd166', life: 0.6, speedMin: 40, speedMax: 180, size: 1.8, additive: true });
      this.addDamageNumber(x, y - 14, `+${reward} ECHOES`, '#ffd166', 1.1, 10);
    });

    // --- cover pillars (CoverSystem) ---
    eventBus.on('pillar:spawned', ({ x, y }) => {
      this._particles.burst(x, y, { count: 6, color: '#8ad1ff', life: 0.3, speedMin: 10, speedMax: 50, size: 1 });
    });
    eventBus.on('pillar:cracked', ({ x, y }) => {
      this._particles.burst(x, y, { count: 5, color: '#8ad1ff', life: 0.25, speedMin: 40, speedMax: 110, size: 1, shape: 'spark', additive: true });
    });
    eventBus.on('pillar:shattered', ({ x, y }) => {
      this._particles.burst(x, y, { count: 16, color: '#8ad1ff', life: 0.4, speedMin: 40, speedMax: 160, size: 1.5 });
      this._particles.burst(x, y, { count: 8, color: '#ffffff', life: 0.5, speedMin: 20, speedMax: 90, size: 1.2, shape: 'square', additive: true, drag: 1.6, gravity: 30 });
      this.addTrauma(0.2);
    });
    eventBus.on('player:died', () => {
      this.addTrauma(1);
      this.slowMo(0.35, 0.9);
      this.flashScreen('#ff3b3b', 0.25, 0.5);
    });

    // --- boss beats ---
    eventBus.on('boss:phase-change', ({ phase, boss }) => {
      this.bossMotion.attackTime = 1.2;
      this.phasePulse = 1.2;
      // The Unmade King's true-form reveal — the same phase threshold every
      // boss (heralds included) has, just with a much bigger moment attached
      // for this one boss specifically. boss.bossDef (not the game-level
      // bossDef) and the explicit !isMiniboss both matter: without them any
      // herald hitting its own phase 2 — including King's Harbinger, whose
      // def is spread from the real final boss and still carries
      // isFinalBoss — would incorrectly trigger this.
      const isFinalFormChange = !boss.bossDef.isMiniboss && boss.bossDef.isFinalBoss && phase === 2;
      this._particles.burst(boss.x, boss.y, {
        count: isFinalFormChange ? 80 : 30,
        color: '#ffffff',
        life: isFinalFormChange ? 0.9 : 0.5,
        speedMin: 40,
        speedMax: isFinalFormChange ? 220 : 160,
        size: isFinalFormChange ? 3 : 2,
      });
      this._particles.burst(boss.x, boss.y, {
        count: isFinalFormChange ? 24 : 10, color: HIT_SPARK_COLOR, life: 0.5,
        speedMin: 100, speedMax: 240, size: 1.4, shape: 'spark', additive: true,
      });
      this.addTrauma(isFinalFormChange ? 1 : 0.6);
      this.triggerHitStop(isFinalFormChange ? 0.2 : 0.08);
      this.flashScreen('#ffffff', isFinalFormChange ? 0.3 : 0.14, isFinalFormChange ? 0.4 : 0.25);
      if (isFinalFormChange) this.slowMo(0.35, 0.5);
    });
    eventBus.on('boss:pattern-start', ({ name, telegraphDuration }) => {
      this.telegraphCallout = { name, remaining: telegraphDuration };
      this.bossMotion.attackTime = 0.45;
    });
    // A spell card survived clean (BULLET-HELL.md §D1). The whole mechanic is
    // the game finally saying "that specific thing you just did was hard" —
    // so it gets a title card, its own colour, and a spark burst on the
    // player, who is where the skill happened. Deliberately NO hitstop or
    // slow-mo: a capture resolves while the next pattern is already
    // telegraphing, and interrupting the player's dodge to congratulate them
    // would be the reward getting them hit.
    eventBus.on('boss:pattern-captured', ({ name, glimmer, grazes, desperation }) => {
      const life = CAPTURE.bannerDuration * (desperation ? 1.3 : 1);
      this.captureBanner = { name, glimmer, grazes, desperation, remaining: life, maxLife: life };
      const player = this._getPlayer();
      if (player) {
        this._particles.burst(player.x, player.y, {
          count: desperation ? 30 : 16,
          color: CAPTURE.color,
          life: 0.55,
          speedMin: 40,
          speedMax: desperation ? 200 : 140,
          size: 1.5,
          shape: 'spark',
          additive: true,
        });
        this.addDamageNumber(player.x, player.y - 12, 'CAPTURED', CAPTURE.color, 0.8, 14);
      }
      this.flashScreen(CAPTURE.color, desperation ? 0.18 : 0.1, 0.28);
    });
    // The reward window opening after a signature attack — call it out so
    // "hit it NOW" reads without knowing the green ring convention yet.
    eventBus.on('boss:vulnerability-open', () => {
      const boss = this._getBoss();
      if (boss) this.addDamageNumber(boss.x, boss.y - boss.radius - 8, 'EXPOSED', '#4ade80', 0.7, 10);
    });
    // The survive-the-finale window beginning: the boss hit 0 HP and went
    // invulnerable — the single hardest gear-shift in a fight, sold with
    // the heaviest time-feel stack in the game.
    eventBus.on('boss:desperation-start', () => {
      const boss = this._getBoss();
      if (boss) {
        this._particles.burst(boss.x, boss.y, { count: 36, color: '#ff3b6b', life: 0.6, speedMin: 60, speedMax: 220, size: 2, shape: 'spark', additive: true });
      }
      this.addTrauma(0.7);
      this.triggerHitStop(0.12);
      this.slowMo(0.4, 0.5);
      this.flashScreen('#ff3b6b', 0.3, 0.45);
    });
    eventBus.on('desperation:end', () => {
      this.addTrauma(0.25);
      this.flashScreen('#4ade80', 0.12, 0.3);
    });

    // --- player moments ---
    eventBus.on('player:dash-start', ({ x, y }) => {
      this._particles.burst(x, y, { count: 6, color: '#5ad1e6', life: 0.2, speedMin: 10, speedMax: 40, size: 1 });
      this.dashAfterimages.push({ x, y, life: 0.28, maxLife: 0.28 });
    });
    eventBus.on('player:dash-refilled', () => {
      this.dashRefillFlash = 0.4;
    });
    // Kestrel's dash-strike connecting (see CombatSystem._applyDashAoe) — a
    // big impact burst + shake + hit-stop so slamming into an enemy lands.
    eventBus.on('dash:hit', ({ x, y }) => {
      this._particles.burst(x, y, { count: 16, color: '#5ad1e6', life: 0.35, speedMin: 60, speedMax: 200, size: 2 });
      this._particles.burst(x, y, { count: 8, color: HIT_SPARK_COLOR, life: 0.22, speedMin: 100, speedMax: 240, size: 1.3, shape: 'spark', additive: true });
      this.addTrauma(0.5);
      this.triggerHitStop(0.07);
    });
    // --- champion signatures & ultimates (data/characters.js, data/ultimates.js) ---
    // Iris's Killing Edge: a crit has to be legible as a crit, or the whole
    // mechanic is invisible. Bigger, hotter, longer-lived number than the
    // standard one, plus its own spark ring.
    eventBus.on('combat:crit', ({ x, y, amount }) => {
      this.addDamageNumber(x, y - 10, `${Math.ceil(amount)}!`, '#ff87b3', 0.8, 16);
      this._particles.burst(x, y, {
        count: 7, color: '#ff87b3', life: 0.28, speedMin: 90, speedMax: 220, size: 1.3, shape: 'spark', additive: true,
      });
    });
    // Rook's Aegis plate eating a hit — a hard, metallic "that didn't get
    // through" beat, deliberately shaped like the player:damaged reaction
    // (same moment) but green and without the red screen flash.
    eventBus.on('player:plate-broken', () => {
      const player = this._getPlayer();
      this._particles.burst(player.x, player.y, {
        count: 14, color: '#7ad67a', life: 0.35, speedMin: 60, speedMax: 170, size: 1.4, shape: 'square', additive: true,
      });
      this.addDamageNumber(player.x, player.y - 8, 'BLOCKED', '#7ad67a', 0.5, 8);
      this.addTrauma(0.25);
      this.triggerHitStop(0.04);
    });
    eventBus.on('player:plate-restored', () => {
      this.addPlayerPulse('#7ad67a', 16, 0.3);
    });
    // Vess's Cadence topping out / breaking — the streak is the champion, so
    // both edges of it get a tell.
    eventBus.on('player:cadence-max', () => {
      const player = this._getPlayer();
      this.addFloatingText(player.x, player.y - player.radius - 6, 'CADENCE', '#5ad1e6');
      this.addPlayerPulse('#5ad1e6', 22, 0.4);
    });
    eventBus.on('player:cadence-broken', () => {
      this.addPlayerPulse('#3a4a5a', 14, 0.25);
    });
    // Kestrel's Slipstream chain — the stack count is what the player is
    // actually playing around, so put the number on screen.
    eventBus.on('dash:chain', ({ stacks, x, y }) => {
      if (stacks > 1) this.addDamageNumber(x, y - 14, `CHAIN x${stacks}`, '#ffd166', 0.6, 12);
    });
    eventBus.on('ultimate:ready', ({ name, color }) => {
      const player = this._getPlayer();
      this.addFloatingText(player.x, player.y - player.radius - 8, `${name.toUpperCase()} READY`, color);
      this.addPlayerPulse(color, 28, 0.5);
    });
    // The single biggest button in a run: full-weight time-feel stack, on the
    // order of a boss phase change.
    eventBus.on('ultimate:activated', ({ name, color, x, y }) => {
      this._particles.burst(x, y, { count: 46, color, life: 0.7, speedMin: 40, speedMax: 220, size: 2.4, additive: true });
      this._particles.burst(x, y, { count: 18, color: '#ffffff', life: 0.4, speedMin: 120, speedMax: 300, size: 1.4, shape: 'spark', additive: true });
      this.addFloatingText(x, y - 14, name.toUpperCase(), color);
      this.addPlayerPulse(color, 56, 0.6);
      // The gold flash the Awakening surge used to own, kept because the
      // surge itself is still what activation grants (Player.isSurging) — it
      // layers under the def's own color so both reads are present.
      this.flashScreen('#ffd166', 0.12, 0.25);
      this.addTrauma(0.7);
      this.triggerHitStop(0.1);
      this.slowMo(0.45, 0.35);
      this.flashScreen(color, 0.22, 0.35);
    });
    eventBus.on('ultimate:ended', ({ color }) => {
      this.addPlayerPulse(color, 22, 0.35);
    });
    // Bastion's slam and each Skybreaker landing — a burst that happens at a
    // point in the world rather than on the player.
    eventBus.on('ultimate:shockwave', ({ x, y, radius, color }) => {
      this._particles.burst(x, y, { count: 20, color, life: 0.45, speedMin: 60, speedMax: Math.min(280, radius * 3), size: 1.8, additive: true });
      this._particles.burst(x, y, { count: 10, color: HIT_SPARK_COLOR, life: 0.25, speedMin: 100, speedMax: 240, size: 1.2, shape: 'spark', additive: true });
      this.addPulse(x, y, color, radius, 0.4);
      this.addTrauma(0.35);
    });

    // Bullet cancel (BULLET-HELL.md §D2). Game already sparks each converted
    // bullet; this is the whole-screen half of the beat, and it scales with
    // how full the screen was — cancelling six bullets should barely register,
    // cancelling sixty should feel like the room was cleared for you.
    eventBus.on('bullets:cancelled', ({ count, x, y }) => {
      const weight = Math.min(1, count / 45);
      if (weight <= 0) return;
      this.addPulse(x, y, '#8ad1ff', 90 + weight * 220, 0.35 + weight * 0.2);
      this.addTrauma(0.1 + weight * 0.2);
      this.flashScreen('#8ad1ff', 0.05 + weight * 0.1, 0.22);
    });

    eventBus.on('player:revived', () => {
      const player = this._getPlayer();
      this._particles.burst(player.x, player.y, { count: 50, color: '#7e97ff', life: 0.7, speedMin: 30, speedMax: 160, size: 2.5 });
      this.addTrauma(0.5);
      this.addPlayerPulse('#7e97ff', 38, 0.65);
      this.flashScreen('#7e97ff', 0.2, 0.4);
    });
    // Health pod (immediate, one-time) and Vampiric (throttled/accumulated —
    // see Player.js's _flushLifesteal) share this single event so both read
    // as "you just healed" the same way.
    eventBus.on('player:healed', ({ amount, source }) => {
      if (amount <= 0) return;
      const player = this._getPlayer();
      this.addDamageNumber(player.x, player.y - player.radius - 6, `+${Math.ceil(amount)}`, '#4ade80', 0.65, 10);
      this._particles.burst(player.x, player.y, {
        count: source === 'pod' ? 26 : 10, color: '#4ade80', life: 0.45, speedMin: 20, speedMax: 100, size: 1.5,
      });
      this.healGlowTimer = 0.4;
    });
    // Fires on every level gained (see ProgressionSystem._addXp), whether
    // it opens a real pick screen or silently auto-boosts once maxed out.
    eventBus.on('xp:levelup:gained', () => {
      const player = this._getPlayer();
      const levelUpColor = this._fxColor ?? '#ffd166';
      this.addFloatingText(player.x, player.y - player.radius - 6, 'LEVEL UP!', levelUpColor);
      this._particles.burst(player.x, player.y, { count: 14, color: levelUpColor, life: 0.5, speedMin: 40, speedMax: 120, size: 1.3, shape: 'spark', additive: true });
    });
    // A level-up threshold crossed at the exact moment a boss died — the
    // pick screen never got a chance to show (ProgressionSystem.
    // forceResolvePending, LevelUpPicker's boss:died handler tears it down
    // first) so surface what got auto-granted instead of leaving the
    // "LEVEL UP!" text above as the only feedback.
    eventBus.on('xp:levelup:auto-resolved', ({ labels }) => {
      const player = this._getPlayer();
      labels.forEach((label, i) => {
        this.addDamageNumber(player.x, player.y - player.radius - 20 - i * 14, `+${label}`, '#ffd166', 1.4, 14);
      });
    });

    // --- gauntlet interloper signatures (2026-07 bosses) ---
    // The Famished Court devouring a courtier: the heal reads as a moment
    // ON the boss (green number, bile gulp burst) — the mirror image of
    // the player's own heal feedback, which is exactly the mental model
    // the Feast wants the player to form: "it just ate my kill."
    eventBus.on('boss:devoured', ({ x, y, bossX, bossY, amount }) => {
      this._particles.burst(x, y, { count: 10, color: '#cdd45a', life: 0.35, speedMin: 30, speedMax: 110, size: 1.4 });
      if (amount > 0) this.addDamageNumber(bossX, bossY - 24, `+${Math.ceil(amount)}`, '#4ade80', 0.8, 12);
      this.addTrauma(0.15);
    });
    // Courtiers still marching when the feast ends scurry off — a small
    // puff each so they don't just blink out of existence.
    eventBus.on('feast:ended', ({ fled }) => {
      for (const { x, y } of fled) {
        this._particles.burst(x, y, { count: 6, color: '#cdd45a', life: 0.3, speedMin: 20, speedMax: 70, size: 1 });
      }
    });
    // The Grand Pause releasing: every frozen bullet surging at once is the
    // Iron Cadence's biggest moment — flash cold steel, not warning red.
    eventBus.on('cadence:surge', () => {
      this.addTrauma(0.3);
      this.flashScreen('#9fb8e8', 0.14, 0.25);
    });
    // A broken Vow: the seal the player abandoned erupting behind them.
    eventBus.on('vow:broken', ({ x, y }) => {
      this._particles.burst(x, y, { count: 18, color: '#ff87b3', life: 0.45, speedMin: 60, speedMax: 200, size: 1.6, shape: 'spark', additive: true });
      this.addTrauma(0.35);
    });

    // --- pickups / waves / ascension modifiers ---
    eventBus.on('pod:spawned', ({ x, y }) => {
      this._particles.burst(x, y, { count: 10, color: '#4ade80', life: 0.5, speedMin: 8, speedMax: 30, size: 1, additive: true });
    });
    // Health pods previously despawned with zero feedback — picking one up
    // looked identical to just walking past it. No shake/hit-stop since
    // this is a calm, positive moment, not an impact.
    eventBus.on('pod:collected', ({ x, y }) => {
      this._particles.burst(x, y, { count: 20, color: '#4ade80', life: 0.5, speedMin: 30, speedMax: 140, size: 2 });
      this._particles.burst(x, y, { count: 8, color: '#ffffff', life: 0.35, speedMin: 40, speedMax: 110, size: 1, shape: 'spark', additive: true });
    });
    // A Bomber's death burst — visual half only; the actual AoE damage
    // check stays in Game.js, which has the live player reference.
    eventBus.on('minion:exploded', ({ x, y }) => {
      this._particles.burst(x, y, { count: 20, color: '#ff8a4a', life: 0.45, speedMin: 50, speedMax: 170, size: 2 });
      this._particles.burst(x, y, { count: 8, color: HIT_SPARK_COLOR, life: 0.3, speedMin: 90, speedMax: 210, size: 1.3, shape: 'spark', additive: true });
    });
    // A suicide burst (BULLET-HELL.md §D6). This lands on the same frame as
    // the red minion:died puff above, so it exists purely to answer "where did
    // those bullets come from" — a pop in the bullets' own hue, so the corpse
    // that authored them is identifiable in a crowded clear. No trauma or
    // hit-stop: the kill already earned its shake, and doubling it would make
    // killing things in Act II feel heavier than killing them in Act I.
    eventBus.on('minion:death-burst', ({ x, y }) => {
      this._particles.burst(x, y, { count: 10, color: SUICIDE.color, life: 0.35, speedMin: 40, speedMax: 130, size: 1.4 });
    });
    // XP motes collected (BULLET-HEAVEN.md §H1). Sized by how many landed in
    // the tick: one mote is a spark, a stage-clear vacuum is a shower. Kept
    // cheap deliberately — this is the highest-frequency reward event in the
    // game by an order of magnitude, and it fires hardest exactly when the
    // screen is already fullest.
    eventBus.on('mote:collected', ({ x, y, count }) => {
      const player = this._getPlayer();
      this._particles.burst(player.x, player.y, {
        count: Math.min(12, 1 + count),
        color: '#8ad1ff',
        life: 0.25,
        speedMin: 20,
        speedMax: 70,
        size: 1,
        shape: 'spark',
        additive: true,
      });
      // Only a real haul gets the pulse — otherwise the player ring would
      // strobe continuously through any wave stage.
      if (count >= 5) this.addPlayerPulse('#8ad1ff', 18 + Math.min(20, count), 0.3);
      if (count >= 20) this.addFloatingText(x, y - 10, `+${count}`, '#8ad1ff');
    });

    eventBus.on('wave:started', () => {
      this.phasePulse = Math.max(this.phasePulse, 0.5);
    });
    eventBus.on('wave:cleared', () => {
      const player = this._getPlayer();
      this.addFloatingText(player.x, player.y - player.radius - 6, 'WAVE CLEARED', '#ffd166');
      this._particles.burst(player.x, player.y, { count: 10, color: '#ffd166', life: 0.4, speedMin: 30, speedMax: 100, size: 1.2, shape: 'spark', additive: true });
    });
    eventBus.on('ascension:enrage', () => {
      const boss = this._getBoss();
      if (boss) this.addDamageNumber(boss.x, boss.y - boss.radius - 8, 'ENRAGED', '#ff3b3b', 0.9, 10);
      this.addTrauma(0.5);
      this.flashScreen('#ff3b3b', 0.2, 0.35);
    });
    eventBus.on('ascension:arena-pulse', () => {
      this.phasePulse = Math.max(this.phasePulse, 0.8);
      this.addTrauma(0.3);
      this.flashScreen('#c9a8ff', 0.14, 0.3);
    });
  }
}
